import React, { useMemo, useState } from 'react';
import { ChevronDown, ChevronUp, Pencil, X } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import {
  DEFAULT_PIPE_GROUP_BY,
  getPipeGroupFieldLabel,
  isPipeGroupHierarchyField,
  MAX_PIPE_GROUP_LEVELS,
  normalizePipeGroupBy,
  PIPE_GROUP_FIELD_OPTIONS,
  type PipeGroupField,
} from '../utils/groupPipeMaterials';
import { cn } from '../../../lib/utils';

type PipeGroupByControlProps = {
  groupByFields: PipeGroupField[];
  onChange: (fields: PipeGroupField[]) => void;
  enabled: boolean;
  onEnabledChange: (enabled: boolean) => void;
};

export const PipeGroupByControl: React.FC<PipeGroupByControlProps> = ({
  groupByFields,
  onChange,
  enabled,
  onEnabledChange,
}) => {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<PipeGroupField[]>(groupByFields);

  const availableToAdd = useMemo(
    () => PIPE_GROUP_FIELD_OPTIONS.filter((o) => !draft.includes(o.value)),
    [draft]
  );

  const summary =
    enabled && groupByFields.length > 0
      ? `Grouped by ${groupByFields.map(getPipeGroupFieldLabel).join(' → ')}`
      : 'Flat list (no grouping)';

  const openEditor = () => {
    setDraft(
      groupByFields.length
        ? normalizePipeGroupBy(groupByFields)
        : [...DEFAULT_PIPE_GROUP_BY]
    );
    setOpen(true);
  };

  const move = (index: number, dir: -1 | 1) => {
    const field = draft[index];
    if (isPipeGroupHierarchyField(field)) return;
    const next = [...draft];
    const target = index + dir;
    if (target < 0 || target >= next.length) return;
    if (isPipeGroupHierarchyField(next[target])) return;
    const tmp = next[index];
    next[index] = next[target];
    next[target] = tmp;
    setDraft(next);
  };

  const removeAt = (index: number) => {
    const field = draft[index];
    if (isPipeGroupHierarchyField(field)) return;
    setDraft((prev) => prev.filter((_, i) => i !== index));
  };

  const addField = (field: PipeGroupField) => {
    setDraft((prev) => {
      if (prev.includes(field) || prev.length >= MAX_PIPE_GROUP_LEVELS) return prev;
      return [...prev, field];
    });
  };

  const apply = () => {
    const normalized = normalizePipeGroupBy(draft);
    onChange(normalized);
    onEnabledChange(normalized.length > 0);
    setOpen(false);
  };

  const resetDefault = () => {
    setDraft([...DEFAULT_PIPE_GROUP_BY]);
  };

  const clearGrouping = () => {
    onChange([]);
    onEnabledChange(false);
    setOpen(false);
  };

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs text-heritage-grey">{summary}</p>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            type="button"
            size="sm"
            className="bg-hero-red gap-2 text-white hover:bg-hero-red-60"
            onClick={openEditor}
            title="Edit grouping"
            aria-label="Edit grouping"
          >
            <Pencil className="h-4 w-4" />
            Grouping
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className={cn(
              enabled
                ? 'border-[#E2E5EA] text-blueprint-blue hover:bg-gray-50'
                : 'border-blueprint-blue bg-blueprint-blue text-white hover:opacity-90 hover:bg-blueprint-blue'
            )}
            onClick={() => {
              if (enabled) {
                onEnabledChange(false);
              } else {
                if (!groupByFields.length) onChange([...DEFAULT_PIPE_GROUP_BY]);
                onEnabledChange(true);
              }
            }}
          >
            {enabled ? 'Show flat list' : 'Enable grouping'}
          </Button>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg flex flex-col gap-0 p-0 overflow-hidden">
          <DialogHeader className="border-b border-[#E2E5EA] px-6 py-4 pr-12">
            <DialogTitle className="text-blueprint-blue">Group by (ordered)</DialogTitle>
            <p className="mt-1 text-xs font-normal text-heritage-grey">
              Hierarchy is fixed: Project Name → Heat Number → Pipe Id → Pipe No. You can add
              optional fields below; those can be reordered or removed.
            </p>
          </DialogHeader>

          <div className="max-h-[60vh] space-y-4 overflow-y-auto px-6 py-4">
            <div className="space-y-2">
              {draft.length === 0 && (
                <p className="text-sm text-heritage-grey">No group fields — table will be flat.</p>
              )}
              {draft.map((field, index) => {
                const locked = isPipeGroupHierarchyField(field);
                return (
                  <div
                    key={`${field}-${index}`}
                    className="flex items-center justify-between gap-2 rounded-lg border border-[#E2E5EA] bg-gray-50 px-3 py-2"
                  >
                    <span className="text-sm font-medium text-blueprint-blue">
                      {index + 1}. {getPipeGroupFieldLabel(field)}
                    </span>
                    {locked ? null : (
                      <div className="flex items-center gap-1">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="h-8 w-8 p-0"
                          disabled={index === 0 || isPipeGroupHierarchyField(draft[index - 1])}
                          onClick={() => move(index, -1)}
                          aria-label="Move up"
                        >
                          <ChevronUp className="h-4 w-4" />
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="h-8 w-8 p-0"
                          disabled={
                            index === draft.length - 1 ||
                            isPipeGroupHierarchyField(draft[index + 1])
                          }
                          onClick={() => move(index, 1)}
                          aria-label="Move down"
                        >
                          <ChevronDown className="h-4 w-4" />
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="h-8 w-8 p-0 text-red-600 hover:bg-red-50"
                          onClick={() => removeAt(index)}
                          aria-label="Remove"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <div>
              <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-heritage-grey">
                Add field
              </label>
              {draft.length >= MAX_PIPE_GROUP_LEVELS ? (
                <p className="text-sm text-heritage-grey">
                  Maximum {MAX_PIPE_GROUP_LEVELS} levels reached. Remove one to add another.
                </p>
              ) : availableToAdd.length === 0 ? (
                <p className="text-sm text-heritage-grey">All group fields are already in use.</p>
              ) : (
                <div className="flex flex-wrap gap-2 rounded-lg border border-[#E2E5EA] bg-gray-50 p-3">
                  {availableToAdd.map((o) => (
                    <button
                      key={o.value}
                      type="button"
                      className="rounded-lg border border-[#E2E5EA] bg-white px-3 py-2 text-sm font-medium text-blueprint-blue shadow-sm hover:border-blueprint-blue"
                      onClick={() => addField(o.value)}
                    >
                      + {o.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          <DialogFooter className="flex flex-shrink-0 flex-wrap gap-2 border-t border-[#E2E5EA] bg-page-bg px-6 py-4 sm:justify-start">
            <Button
              type="button"
              size="sm"
              className="bg-hero-red text-white hover:bg-hero-red/90"
              onClick={apply}
            >
              Apply
            </Button>
            <Button type="button" variant="outline" size="sm" onClick={resetDefault}>
              Reset default
            </Button>
            <Button type="button" variant="outline" size="sm" onClick={clearGrouping}>
              Clear grouping
            </Button>
            <Button type="button" variant="outline" size="sm" onClick={() => setOpen(false)}>
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
